import { useContext } from 'react';
import * as wasm from 'sched';
import { WorkspaceContext } from '../context/WorkspaceContext';
import { useDatabaseEntity } from './useDatabaseEntity';

export interface ProjectInfo {
  id: string;
  name: string;
}

export interface TaskDetails {
  id: string;
  title: string;
  description: string | null;
  estimates: wasm.WorkEstimate[];
  deadlines: wasm.Deadline[];
  remoteReference?: wasm.RemoteReference;
  complete: boolean;
  project?: ProjectInfo;
}

export function useTask(taskId: string | undefined) {
  const { safeData } = useContext(WorkspaceContext);
  const { entity, loading, error } = useDatabaseEntity<TaskDetails>(taskId, {
    entityType: 'Task',
    deps: [safeData],
    build: async (database, id) => {
      const obj = database.tasks()[id];
      if (!obj) return null;

      // Read sync fields up front so we always have a base object to render
      // even if the async enrichment below fails.
      const base: TaskDetails = {
        id: obj.id(),
        title: obj.title(),
        description: obj.description() || null,
        estimates: obj.estimates(),
        deadlines: obj.deadlines(),
        remoteReference: undefined,
        complete: obj.complete(),
        project: undefined,
      };

      let remoteReference: wasm.RemoteReference | undefined;
      try {
        remoteReference = await obj.reference();
      } catch (err) {
        console.error('Error fetching task remote reference:', err);
      }

      let project: ProjectInfo | undefined;
      try {
        if (safeData) {
          const projectId = await obj.project_id(safeData);
          if (projectId) {
            const projectData = database.projects()[projectId];
            if (projectData) {
              project = { id: projectId, name: projectData.name() };
            }
          }
        }
      } catch (err) {
        console.error('Error fetching task project:', err);
      }

      return { ...base, remoteReference, project };
    },
  });
  return { task: entity, loading, error };
}
